import { resolveFleetCardSlug } from "../utils/fleetCardSlug";
import { PAGE_SCHEMAS } from "./seoSchemas";

const SITE_ORIGIN = "https://www.indiaboats.in";

export const DEFAULT_OG_IMAGE = "/assets/img/Luxury_Yacht_Charters.png";

export const resolveOgImageUrl = (imagePath) => {
  const value = String(imagePath || DEFAULT_OG_IMAGE).trim();

  if (!value) {
    return `${SITE_ORIGIN}${DEFAULT_OG_IMAGE}`;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  if (value.startsWith("/")) {
    return `${SITE_ORIGIN}${value}`;
  }

  return `${SITE_ORIGIN}/${value}`;
};

const YACHT_PAGE_SEO_CONTEXT = {
  PrivateYacht: {
    category: "Private Yacht Charter",
    primaryKeyword: "private yacht charter Mumbai",
    keywordExtras: [
      "yacht charter Mumbai",
      "private yacht rental Mumbai",
      "luxury yacht Mumbai",
    ],
  },
  YachtParties: {
    category: "Yacht Party",
    primaryKeyword: "yacht party Mumbai",
    keywordExtras: ["boat party Mumbai", "party yacht rental Mumbai"],
  },
  SpeedBoat: {
    category: "Speed Boat Transfer",
    primaryKeyword: "speed boat Mumbai",
    keywordExtras: [
      "speed boat transfer Mumbai",
      "Mandwa speed boat",
      "Alibaug speed boat",
    ],
  },
};

const truncateMeta = (text = "", max = 160) => {
  const clean = String(text).replace(/\s+/g, " ").trim();

  if (!clean) {
    return "";
  }

  if (clean.length <= max) {
    return clean;
  }

  return `${clean.slice(0, max - 3).trim()}...`;
};

export const buildYachtDetailsCanonical = (boatOrSlug = "") => {
  const slug =
    typeof boatOrSlug === "string"
      ? boatOrSlug
      : resolveFleetCardSlug(boatOrSlug);

  if (!slug) {
    return "";
  }

  return `${SITE_ORIGIN}/yachtdetails/${encodeURIComponent(slug)}`;
};

export const buildYachtDetailsSeo = (boat = {}) => {
  const boatName = String(boat.boatName || boat.title || "Yacht").trim();
  const context = YACHT_PAGE_SEO_CONTEXT[boat.pageKey] || {
    category: boat.yachtType || "Yacht Charter",
    primaryKeyword: `${boatName} Mumbai`,
    keywordExtras: ["yacht charter Mumbai", "boat rental Mumbai"],
  };

  const location =
    [boat.placeName, boat.locationName].filter(Boolean).join(" - ") || "Mumbai";
  const capacity = boat.capacity ? ` ${boat.capacity}.` : "";
  const length = boat.length ? ` Size: ${boat.length}.` : "";

  const description = truncateMeta(
    boat.description ||
      `Book ${boatName} for ${context.category.toLowerCase()} in ${location}.${capacity}${length} View photos, timings, pricing and inclusions. Book online with India Boats.`
  );

  const keywords = [
    boatName,
    context.primaryKeyword,
    ...context.keywordExtras,
    location,
    boat.yachtType,
    boat.hullType,
    "India Boats",
  ]
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .join(", ");

  const ogImage =
    boat.photos?.[0] ||
    boat.imgUrl ||
    boat.image ||
    boat.gallery?.[0]?.url ||
    "";

  return {
    title: `${boatName} | ${context.category} | India Boats`,
    description,
    keywords,
    primaryKeyword: boatName || context.primaryKeyword,
    canonical: buildYachtDetailsCanonical(boat),
    ogImage: ogImage || DEFAULT_OG_IMAGE,
  };
};

export const SEO_DATA = {
  home: {
    title: "Yacht Charter Mumbai & Goa | India Boats",
    description:
      "Book private yacht charters, yacht parties, speedboat transfers & corporate sailing events in Mumbai and Goa. Trusted, safe, easy to book.",
    keywords:
      "yacht charter Mumbai, boat rental Mumbai, yacht charter Goa, private yacht party Mumbai, speed boat Mumbai",
    primaryKeyword: "yacht charter Mumbai",
    canonical: "https://www.indiaboats.in/",
    ogImage: "/assets/img/Luxury_Yacht_Charters.png",
    schema: PAGE_SCHEMAS.home,
  },
  about: {
    title: "About India Boats | Founder Mehul Ashar's Story",
    description:
      "India Boats was founded in 2016 by decorated national sailing champion Mehul Ashar. Read our story, safety standards, and why guests trust us.",
    keywords:
      "India Boats founder, Mehul Ashar sailing champion, is India Boats safe, about India Boats",
    primaryKeyword: "about India Boats",
    canonical: "https://www.indiaboats.in/about",
    ogImage: "/assets/img/about1.png",
    schema: PAGE_SCHEMAS.about,
  },
  contact: {
    title: "Contact India Boats | Book a Yacht in Mumbai & Goa",
    description:
      "Call, WhatsApp or email India Boats to book a private yacht charter, yacht party or speedboat transfer in Mumbai or Goa. Fast response.",
    keywords:
      "contact India Boats, India Boats phone number, book yacht Mumbai",
    primaryKeyword: "contact India Boats",
    canonical: "https://www.indiaboats.in/contact",
    ogImage: "/assets/img/ib_red_logo.png",
    schema: PAGE_SCHEMAS.contact,
  },
  packages: {
    title: "Curated Yacht & Boat Packages Mumbai | India Boats",
    description:
      "Browse curated packages — Mandwa day trips, Elephanta Island tours, premium sailing and sunset sails. Complete end-to-end experiences.",
    keywords:
      "yacht packages Mumbai, Mumbai to Mandwa package, Mumbai to Elephanta package, sunset sail Mumbai",
    primaryKeyword: "yacht packages Mumbai",
    canonical: "https://www.indiaboats.in/packages",
    ogImage: "/assets/img/alibaug_package.png",
    schema: PAGE_SCHEMAS.packages,
  },
  privateYachtCharter: {
    title: "Private Yacht Charter Mumbai & Goa | Up to 15 Guests",
    description:
      "Book a private yacht charter in Mumbai or Goa for up to 15 guests. Compare Luxury and Explore fleets, pricing and inclusions.",
    keywords:
      "private yacht charter Mumbai, yacht charter Goa, luxury yacht rental Mumbai, best yacht for small group Mumbai",
    primaryKeyword: "private yacht charter Mumbai",
    canonical: "https://www.indiaboats.in/private-yacht-charter-mumbai-goa",
    ogImage: "/assets/img/privateYacht.png",
    schema: PAGE_SCHEMAS.privateYachtCharter,
  },
  yachtPartyMumbai: {
    title: "Yacht Party Mumbai | Boat Party for 15+ Guests",
    description:
      "Host a yacht party in Mumbai for 15 or more guests. Large party yachts for birthdays, celebrations and group events.",
    keywords:
      "yacht party Mumbai, boat party Mumbai, large group yacht Mumbai",
    primaryKeyword: "yacht party Mumbai",
    canonical: "https://www.indiaboats.in/yacht-party-mumbai",
    ogImage: "/assets/img/networking4.png",
    schema: PAGE_SCHEMAS.yachtPartyMumbai,
  },
  alibaugElephantaBoatTours: {
    title: "Alibaug & Elephanta Boat Tours from Mumbai | Indiaboats",
    description:
      "Book private boat trips from Mumbai to Alibaug, Mandwa and Elephanta with Indiaboats. Enjoy fast speed boat transfers, scenic island escapes and curated day experiences.",
    keywords:
      "Mumbai to Alibaug boat, Mumbai to Mandwa speed boat, Mumbai to Elephanta boat, private boat to Alibaug, speed boat to Mandwa, Elephanta boat tour Mumbai, Alibaug boat tour from Mumbai, private speed boat Mumbai, Mandwa boat booking, Mumbai island tours, things to do in Mumbai, private boat trips Mumbai",
    primaryKeyword: "Mumbai to Alibaug boat",
    canonical: "https://www.indiaboats.in/alibaug-elephanta-boat-tours-mumbai",
    ogImage: "/assets/img/elephanta_package.png",
    schema: PAGE_SCHEMAS.alibaugElephantaBoatTours,
  },
  speedBoat: {
    title: "Speed Boat Transfer Mumbai to Alibaug & Mandwa",
    description:
      "Book a private or shared speed boat transfer from Mumbai to Alibaug or Mandwa. Quick, comfortable, reliable — skip the road traffic.",
    keywords:
      "speed boat Mumbai, speed boat transfer Alibaug, Mandwa speed boat, speed boat Mumbai to Elephanta",
    primaryKeyword: "speed boat Mumbai",
    canonical: "https://www.indiaboats.in/speedboat",
    ogImage: "/assets/img/speed_boat_img1.png",
    schema: PAGE_SCHEMAS.speedBoat,
  },
  corporateEvents: {
    title: "Corporate Yacht Events & Sailing Experiences in Mumbai | Indiaboats",
    description:
      "Plan corporate yacht events, sailing regattas, networking experiences, awards evenings and company celebrations in Mumbai with Indiaboats. Premium on-water events with smooth planning and guest safety.",
    keywords:
      "corporate events Mumbai, corporate yacht events Mumbai, corporate yacht party Mumbai, corporate sailing Mumbai, corporate sailing regatta Mumbai, team building activities Mumbai, corporate team building Mumbai, networking events Mumbai, client entertainment Mumbai, corporate party venue Mumbai, corporate awards party Mumbai, yacht for corporate event Mumbai, business networking yacht Mumbai",
    primaryKeyword: "corporate events Mumbai",
    canonical: "https://www.indiaboats.in/corporate-events",
    ogTitle: "Corporate Events on Yachts in Mumbai | Indiaboats",
    ogDescription:
      "From sailing regattas and team building to yacht parties, awards and executive networking, create a corporate experience your guests will remember.",
    ogImage: "/assets/img/corporate_events4.png",
    schema: PAGE_SCHEMAS.corporateEvents,
  },
  corporateSailingRegatta: {
    title: "Corporate Sailing Regatta Mumbai | Team Building",
    description:
      "Run a corporate sailing regatta in Mumbai for team building, leadership training or client entertainment. Custom B2B packages.",
    keywords:
      "corporate sailing regatta Mumbai, corporate team building Mumbai, sailing regatta corporate event",
    primaryKeyword: "corporate sailing regatta Mumbai",
    canonical: "https://www.indiaboats.in/corporate-sailing-regatta-mumbai",
    ogImage: "/assets/img/regatta1.png",
    schema: PAGE_SCHEMAS.corporateSailingRegatta,
  },
  corporatePartyAwards: {
    title: "Corporate Party & Awards Yacht Mumbai",
    description:
      "Celebrate company milestones, awards nights and corporate parties on a private yacht in Mumbai — a more memorable setting than a banquet hall.",
    keywords:
      "corporate party yacht Mumbai, company awards event Mumbai, corporate celebration yacht Mumbai",
    primaryKeyword: "corporate party yacht Mumbai",
    canonical: "https://www.indiaboats.in/corporate-party-awards-yacht-mumbai",
    ogImage: "/assets/img/corporate_events4.png",
    schema: PAGE_SCHEMAS.corporatePartyAwards,
  },
  corporateEventsNetworking: {
    title: "Corporate Yacht Events Mumbai | Client Networking",
    description:
      "Host a corporate yacht event in Mumbai for client entertainment, business networking or team celebrations. Full event coordination.",
    keywords:
      "corporate yacht event Mumbai, corporate networking event Mumbai, client entertainment yacht Mumbai",
    primaryKeyword: "corporate yacht event Mumbai",
    canonical: "https://www.indiaboats.in/corporate-events-networking-yacht-mumbai",
    ogImage: "/assets/img/networking4.png",
    schema: PAGE_SCHEMAS.corporateEventsNetworking,
  },
  birthdayAnniversary: {
    title: "Birthday & Anniversary Yacht Party Mumbai",
    description:
      "Celebrate a birthday or anniversary on a private yacht in Mumbai — cake-cutting, music, sunset views and photos.",
    keywords:
      "birthday yacht party Mumbai, anniversary yacht Mumbai, yacht celebration Mumbai",
    primaryKeyword: "birthday yacht party Mumbai",
    canonical:
      "https://www.indiaboats.in/birthday-anniversary-yacht-celebration-mumbai",
    ogImage: "/assets/img/birthday3.png",
    schema: PAGE_SCHEMAS.birthdayAnniversary,
  },
  preWedding: {
    title: "Pre-Wedding Yacht Photoshoot Mumbai",
    description:
      "Book a private yacht for your pre-wedding photoshoot in Mumbai. Stunning sea backdrops and skyline views for a romantic shoot.",
    keywords:
      "pre-wedding shoot yacht Mumbai, yacht photoshoot Mumbai, pre-wedding shoot boat Mumbai",
    primaryKeyword: "pre-wedding shoot yacht Mumbai",
    canonical: "https://www.indiaboats.in/pre-wedding-yacht-photoshoot-mumbai",
    ogImage: "/assets/img/boat1.jpg",
    schema: PAGE_SCHEMAS.preWedding,
  },
  familyPrivateYachtParty: {
    title: "Family Yacht Party Mumbai | Private Boat Outings",
    description:
      "Plan a private family yacht outing in Mumbai — away from the crowds, with comfort, privacy and sea views.",
    keywords:
      "family yacht party Mumbai, family boat trip Mumbai, private family outing yacht Mumbai",
    primaryKeyword: "family yacht party Mumbai",
    canonical: "https://www.indiaboats.in/family-private-yacht-party-mumbai",
    ogImage: "/assets/img/family4.png",
    schema: PAGE_SCHEMAS.familyPrivateYachtParty,
  },
  privacy: {
    title: "Privacy Policy | India Boats",
    description:
      "India Boats' privacy policy — how we collect, use and protect your personal information when you enquire or book with us.",
    keywords: "India Boats privacy policy",
    primaryKeyword: "India Boats privacy policy",
    canonical: "https://www.indiaboats.in/privacy",
    ogImage: DEFAULT_OG_IMAGE,
  },
  terms: {
    title: "Terms of Use & Booking Terms | India Boats",
    description:
      "Terms of use and booking terms for India Boats charters, packages and services in Mumbai and Goa.",
    keywords: "India Boats terms of use, India Boats booking terms",
    primaryKeyword: "India Boats terms of use",
    canonical: "https://www.indiaboats.in/terms",
    ogImage: DEFAULT_OG_IMAGE,
  },
  refund: {
    title: "Refunds & Cancellation Policy | India Boats",
    description:
      "India Boats' refund and cancellation policy — timelines, eligibility and process for cancelling or rescheduling a booking.",
    keywords: "India Boats refund policy, India Boats cancellation policy",
    primaryKeyword: "India Boats refund policy",
    canonical: "https://www.indiaboats.in/refund",
    ogImage: DEFAULT_OG_IMAGE,
  },
  dataDeletion: {
    title: "Data Deletion Instructions | India Boats",
    description: "Request deletion of personal data held by India Boats.",
    keywords: "India Boats data deletion, delete personal data India Boats",
    primaryKeyword: "India Boats data deletion",
    canonical: "https://www.indiaboats.in/data-deletion",
    ogImage: DEFAULT_OG_IMAGE,
  },
  yachtDetailsLoading: {
    title: "Yacht Details | India Boats",
    description:
      "View yacht photos, timings, pricing and inclusions. Book your private charter, yacht party or speed boat transfer with India Boats.",
    keywords:
      "yacht details Mumbai, book yacht Mumbai, India Boats yacht charter",
    primaryKeyword: "yacht details Mumbai",
    canonical: "",
    ogImage: DEFAULT_OG_IMAGE,
  },
};
